import { SupportedSoilTextures } from '@app/_components/lf-map/mapTypes'
import { IActivityMap, TemplateInfo } from '@app/activity/_components/dnd'
import { ICropSummary } from '@stores/CropSummary'
import { AnyJSONValue, IGeometryCollection } from '@stores/GeoJSON'
import { SnapshotIn } from 'mobx-state-tree'

export type QrType = { [key: string]: { id: string; name: string; qr: string } }

export type SupportedLanguageKeys = 'en' | 'th' | 'vi' | 'jp'

export enum VIAPIResponse {
  NDVI = 'Ndvi',
  NDMI = 'Ndmi',
  EVI = 'Evi',
  S2MULTI = 'S2multi',
}

export type CropProgressResponse =
  | 'created'
  | 'planted'
  | 'ready_to_harvest'
  | 'harvested'
  | 'overdue'

export type UserResponse = {
  id: string
  name: string
  email: string
  phone: string
  lang: SupportedLanguageKeys // allowed values are [en, th, vi, jp] defaults to en
  area_unit: 'm2' | 'rai' | 'ha' | '10a' | 'ac' // allowed values are [m2, rai, hectares, 10 ares, acres] defaults to m2
  weight_unit: 'ton' | 'kg' // ton and kg can be saved in the user settings. But currently backend always return tons for the values so it needs to be converted in kg in UI.
  soil_units: { [key: string]: string }
}

export interface OrganizationStats {
  total_fields: number
  total_cropzones_area: number
  total_fields_area: number
  total_fields_per_country?: { [key: string]: number }
}

export interface OrganizationResponse {
  id: string
  name: string
  email: string
  address?: string
  phone?: string
  prod_targets?: {
    daily_min?: string
    daily_max?: string
  }
  location?: {
    type: 'Point'
    coordinates: number[]
  }
  seed_usage_unit: 'bags' | 'kg'
  slug: string
  stats?: OrganizationStats
  crop_summary?: ICropSummary[]
  created_at: string
  features: { [key: string]: boolean }
}

export type InviteResponse = {
  id: string
  status: string
  type: string
  role: string
  last_access_at: string
  created_at: string
  updated_at: string
  user: UserResponse
  organization: OrganizationResponse
}

// Returned by GET /organizations/:id/line_users — InviteResponse augmented with
// the user's LINE id (only set when the binding-code flow has been redeemed).
export type LineUserResponse = InviteResponse & {
  line_id: string | null
}

// Returned by POST /organizations/:id/invites/:invite_id/binding_code.
export type BindingCodeResponse = {
  id: string
  code: string
  invite_id: string
  organization_id: string
  expires_at: string
  expires_in: number
  redeemed_at: string | null
}

export type FieldResponse = {
  id: string
  farm_id: string
  name: string
  area: number
  note: string
  timezone: string
  location: {
    centroid: {
      type: 'Point'
      coordinates: number[]
    }
    boundary: {
      type: 'Polygon'
      coordinates: number[][][]
    }
  }
  farm?: FarmResponse
  created_at: string
  stats?: {
    production: number
  }
}

export type FieldGeoJSON = {
  id: string
  geometry: SnapshotIn<IGeometryCollection>
  properties: AnyJSONValue
}

export type ActivityResponse = {
  id: string
  status: string
  extra: any
  activity_type: {
    id: string
    name: string
    canonical_name: string
    technique?: string
    extra: any
  }
  crops: string[]
  note?: string | null
  observational?: boolean
  start_date: string
  end_date?: string
  field?: {
    id: string
    name: string
  }
  type: 'CropzoneActivity' | 'FarmActivity'
  attachments: { id: string; url: string }[]
  cropzone?: CropzoneResponse
  farm: OrganizationResponse
  assignees: { id: string; name: string | null; email: string; type: string }[]
  labels: { id: string; name: string }[]
  template?: {
    id: string
    name: string
  } | null
}

export type FollowResponse = {
  id: string
  follow_method?: { [key: string]: string }
  organization_right: OrganizationResponse
  organization_left: OrganizationResponse
  organization: OrganizationResponse
  status: string
  created_at: string
}

type Task = {
  activity_type_id?:
    | {
        label: string
        value: string
      }
    | undefined
  dap?: number | null
  note?: string
}

export type TemplateResponse = {
  id: string
  name: string
  tasks_count: number
  tasks: Task[]
  assignments_count: number
  created_at: string
  updated_at: string
}

export type AssignmentTemplateReponse = {
  id: string
  name: string
}

export type AssignmentResponse = {
  id: string
  name: string
  template: AssignmentTemplateReponse
  created_at: string
  cropzones_count: number
}

export type FollowsResponse = {
  follows: FollowResponse[]
}

export type TemplatesResponse = TemplateResponse[]

export type AssignmentsResponse = AssignmentResponse[]

export type FarmResponse = OrganizationResponse

export interface YieldStatValue {
  label: string
  week: string | null
  year: string
  value: number
  average_yield: number
  month: string | null
  day: string | null
}

export interface YieldStatsResponse {
  target: number
  target_max: number
  target_min: number
  actual: {
    total: number
    average: number
    percentage_of_target: number
    average_yield: number
    percentage_of_target_yield: number
    target_yield: number
    values: YieldStatValue[]
  }
  forecasted: {
    total: number
    values: YieldStatValue[]
  }
  crop: string
  labels: { id: string }[]
}

export interface YieldStatsTransformed {
  target: number
  targetMax: number
  targetMin: number
  actual: {
    total: number
    average: number
    percentageOfTarget: number
    averageYield: number
    percentageOfTargetYield: number
    targetYield: number
    values: {
      label: string
      value: number
      average_yield: number
      year: number
      month: number | null
      day: number | null
      week: number | null
    }[]
  }
  forecasted: {
    total: number
    values: {
      label: string
      value: number
      average_yield: number
      year: number
      month: number | null
      day: number | null
      week: number | null
    }[]
  }
  crop: string
  labels: { id: string }[]
}

export type CropzoneResponse = {
  id: string
  name: string
  area: number
  note: string
  slug: string
  created_at: string
  start_date: string
  end_date: string | null
  seed_usage: number
  crop_progress: CropProgressResponse | null
  seed_usage_unit: string
  planting_date: string | null
  generation: number | null
  country_i18n: { [key in LangKey]: string }
  region_i18n: { [key in LangKey]: string } | null
  district_i18n: { [key in LangKey]: string } | null
  subdistrict_i18n: { [key in LangKey]: string } | null
  prev_cropzone: { id: string }
  next_cropzone: { id: string }
  canopy_cover_percentages?: { [key: string]: number }
  timezone: string
  archived_at: string | null
  crop: CropResponse
  activities_count: number
  soil_samples_count: number
  activities: ActivityResponse[]
  soil_samples: SoilSampleResponse[]
  location: {
    centroid: {
      type: 'Point'
      coordinates: number[]
    }
    boundary: {
      type: 'Polygon'
      coordinates: number[][][]
    }
  }
  predicted: {
    maturity_date: string | null
    maturity_date_computed_at: string | null
    harvest: string | null
    harvest_unit: string | null
    harvest_computed_at: string | null
    yield: string | null
    yield_unit: string | null
    yield_computed_at: string | null
    ccs: string | null
    ccs_computed_at: string | null
    ccs_unit: string | null
  }
  actual: {
    harvesting_date: string | null
    harvest: string | null
    yield: string | null
  }
  field: {
    id: string | null
    name: string | null
  }
  farm: {
    id: string | null
    name: string | null
    phone: string | null
  }
  traceability?: {
    income: number
    income_unit: string
    harvest_destination_places: { id: string }[]
    crop_quality: { [key: string]: number }
  }
  distance: number | null
  rs_stopped_at: string | null
  labels: {
    id: string
    name: string
    color: string
    description: string | null
    read_only: boolean
    organization_id: string
  }[]
  latest_vis: VIResponse[]
  season: Partial<SeasonResponse>
  dap: number | null
  latest_soil_sample_at: string | null
  gdd: {
    current_gdd: number
    target_gdd: number
    percentage: string
    is_matured: boolean
  } | null
}

export type CropzoneGeoJSON = {
  id: string
  geometry: SnapshotIn<IGeometryCollection>
  properties: AnyJSONValue
  crop: CropTransformed
  traceability?: {
    income?: number | null
    income_unit?: string | null
    harvest_destination_places: string[]
    crop_quality?: { [key: string]: number }
  }
  labels: {
    id: string
    name: string
    color: string
    description: string | null
    read_only: boolean
    organization_id: string
  }[]
  season: {
    id: string
    name: string
    datetime: string
    endDatetime: string
  } | null
}

export type ActivityTypeResponse = {
  id: string
  name: string
  canonical_name: string
  extra_schema: any
}

export type VIResponse = {
  id: string
  cropzone_id: string
  cloudy: boolean
  uri: string | null
  date: string
  type: VIAPIResponse
  thresholds: {
    performance?: {
      high: number
      low: number
    }
  }
  extra_info: {
    maskedPercentage: number | null
    isComposite: boolean
    crs: string
    __MASKED_PERCENTAGE?: number
    __SHRINKED_POLYGON_EVI_MEAN?: number
    __SHRINKED_POLYGON_NDVI_MEAN?: number
    __SHRINKED_POLYGON_NDMI_MEAN?: number
  }
  created_at: string
  updated_at: string
  mean?: number
}

export type ViSmoothResponse = {
  ts: number
  value: number
}

export type RegionResponse = {
  [key: string]: string[]
}

export type CountriesResponse = string[]

interface LocalizedName {
  en: string
  native: string
  th?: string
  [key: string]: string | undefined
}

type Country = LocalizedName
type Region = LocalizedName
type District = LocalizedName

export interface AdministrativeLevelsResponse {
  countries: Country[]
  regions: Region[]
  districts: District[]
}

export type LangKey = 'th' | 'ja' | 'en' | 'vi'

export type CropResponse = {
  id: string
  name: string
  variety: string
  genus?: string | null
  canonical_name: string
  name_i18n: { [key in LangKey]: string }
  variety_i18n: { [key in LangKey]: string }
}

export type CropTransformed = {
  id: string
  name: string
  variety: string
  genus: string | null
  canonical_name: string
  rawName: string
}

export type PlaceResponse = {
  id: string
  name: string
  description: string
  phone: string
  type: string
  address: string
  created_at: string
  lnglat: {
    type: string
    coordinates: number[]
  }
  kind: string
  annual_production_target: number | null
  annual_production_target_unit: string
  capacity: number | null
  capacity_unit: string
}

export type OrganizationImportStatus =
  | 'queued'
  | 'processing'
  | 'imported'
  | 'partial'
  | 'failed'

export type OrganizationImportError = string | { file: string; error: string }

export type OrganizationImportResponse = {
  id: string
  filename: string
  status: OrganizationImportStatus
  stats: {
    fields?: number
    cropzones?: number
    soil_samples?: number
    activities?: number
    sheets?: number
  }
  warnings: string[]
  errors?: OrganizationImportError[]
  byte_size: number | null
  uploaded_at: string
  started_at?: string | null
  finished_at?: string | null
  uploaded_by: { id: string; name: string | null; email: string | null } | null
}

export type OrganizationImportQueuedResponse = {
  import_id: string
  status: 'queued'
}

export type OrganizationImportDownloadResponse = {
  id: string
  filename: string
  url: string
  expires_at: string
}

export type TransactionResponse = {
  id: string
  cropzone_id: string
  crop_quality_unit: string | null
  source?: PlaceResponse | null
  destination: PlaceResponse
  datetime: string
  qty_exchanged: string
  extra: { [key: string]: string | number }
}

export type SoilSampleResponse = {
  id: string
  cropzone: { id: string; slug: string }
  sampled_at: string
  location: number[]
  results: {
    soil_texture: SupportedSoilTextures
    analysis_date: string
    elements: {
      unit: string
      value: number
      judgement: string
      codename: string
      label: string
      thresholds?: [number, number][] | []
    }[]
  }
}

export type LabelResponse = {
  id: string
  name: string
  color: string
  description: string | null
  read_only: boolean
  organization_id: string
}

export type SeasonResponse = {
  id: string
  name: string
  start_datetime: string
  end_datetime: string
  country: string | null
  region: string | null
  pending_plantation: boolean
  crop_name: string
  crop_name_i18n: { [key in LangKey]: string }
  status: string
  organization_id: string
}

export interface BoardsResponse {
  id: string
  name: string
  labels: { id: string; name: string }[]
}

export interface BoardGroupResponse {
  group_info: {
    farm: {
      id: string
      name: string
    }
    cropzone: {
      id: string
      name: string
      area: number
      timezone: string
      total_activities: number
    }
    crop: CropResponse
    template: TemplateInfo
  }
  items: IActivityMap
}

export type NotificationResponse = {
  id: string
  type: string
  recipient_type: string
  recipient_id: string
  params: { [key: string]: string }
  level: string
  read_at: string
  created_at: string
  updated_at: string
}

export type NotificationSummaryResponse = {
  total: number | null
  total_unread: number | null
  levels:
    | {
        recipient_id: string
        level: string
        total_count_level: number
        total_unread_count_level: number
        types: { name: string; total: number; total_unread: number }[]
      }[]
    | null
}

export type SankeyResponse = {
  nodes: {
    name: string
    depth?: number
    place?: PlaceResponse
    value: string
  }[]
  links: { source: string; target: string; value: number }[]
}

export interface StatResponse {
  total_sold: string
  total_sold_unit: string
  destinations: {
    place: PlaceResponse
    from_district: string
    from_subdistrict: string
    amount: string
    amount_type: string
    percentage: string
  }[]
  labels: string[]
}

export interface PlantingResponse {
  totals: {
    created: number
    planted: number
    not_planted: number
    harvested: number
    ready_to_harvest: number
  }
  series: {
    label: string
    created: number
    planted: number
    not_planted: number
    harvested: number
    ready_to_harvest: number
    year: string
  }[]
}

export type SeasonTercileForecast =
  | 'EQUAL_CHANCES'
  | 'HIGH_PROB_AN'
  | 'MODERATE_PROB_AN'
  | 'HIGH_PROB_NN'
  | 'MODERATE_PROB_NN'
  | 'HIGH_PROB_BN'
  | 'MODERATE_PROB_BN'

export type SeasonTercileResponse = {
  property: 'precipitation' | 'average_air_temperature'
  start_date: string
  end_date: string
  '5_years_percentile_50': number | null
  probabilities: [number, number, number]
  forecast: SeasonTercileForecast
  suggestion?: string | null
  created_at: string
}

export type SupplyChainSeasonStatus = 'draft' | 'active' | 'closed'

export type SupplyChainQuotaSource =
  | 'manual'
  | 'auto_cropzone'
  | 'auto_last_season'
  | 'auto_even'
  | 'agent_v1'

export type SupplyChainSeasonResponse = {
  id: string
  organization_id: string
  season_year: number
  factory_target_tons: number | null
  daily_target_tons: number | null
  yield_assumption_t_per_rai: number | null
  buffer_ratio: number
  cycle_days_default: number | null
  status: SupplyChainSeasonStatus
  onboarded_mid_season: boolean
  prior_planted_rai: number | null
  prior_harvested_tons: number | null
  factory_place_id: string | null
  notes: string | null
}

export type SupplyChainMonthlyTargetResponse = {
  id: string
  organization_id: string
  season_id: string
  month: number
  planting_target_rai: number | null
  planting_target_rai_lo: number | null
  planting_target_rai_hi: number | null
  harvesting_target_tons: number | null
  is_locked: boolean
}

export type SupplyChainBrokerQuotaResponse = {
  id: string
  organization_id: string
  season_id: string
  month: number
  broker_id: string
  district_id: string
  planting_quota_rai: number | null
  re_quota_rai: number | null
  harvesting_quota_tons: number | null
  cycle_days: number | null
  source: SupplyChainQuotaSource | null
  notes: string | null
}

export type SupplyChainBrokerDistrictResponse = {
  id: string
  district_id: string
  district_en: string
  weight: number | null
  season_year: number | null
}

export type SupplyChainBrokerResponse = {
  id: string
  organization_id: string
  name: string
  region: string | null
  districts: SupplyChainBrokerDistrictResponse[]
}

export type SupplyChainDistrictResponse = {
  id: string
  organization_id: string
  district_en: string
  district_th: string | null
  province_en: string | null
  province_th: string | null
  region: string | null
}

export type SupplyChainCycleLengthResponse = {
  id: string
  organization_id: string
  district_id: string
  planting_month: number
  cycle_days_p10: number | null
  cycle_days_p50: number | null
  cycle_days_p90: number | null
  n_observed: number | null
  source: string | null
}

export type SupplyChainMaturityDayResponse = {
  id: string
  organization_id: string
  district_id: string
  planting_mm_dd: string
  planting_doy: number | null
  est_maturity_days: number | null
  mean_daily_temp: number | null
}

export type SupplyChainDailyHarvestRecordResponse = {
  id: string
  organization_id: string
  broker_id: string
  date: string
  planned_harvest_area_rai: number | null
  estimated_harvest_tons: number | null
  actual_harvest_tons: number | null
  delta_tons: number | null
  is_valid: boolean
  error_reason: string | null
}

export type SupplyChainDailyPlantingRecordResponse = {
  id: string
  organization_id: string
  broker_assignment_id: string
  broker_id: string
  agronomist_id: string
  district_id: string | null
  area_name: string | null
  season_year: number
  date: string
  planted_area_rai: number | null
  is_valid: boolean
  error_reason: string | null
}

export type SupplyChainHarvestProjectionResponse = {
  planting_record_id: string
  organization_id: string
  broker_id: string
  agronomist_id: string
  district_id: string | null
  district_source: 'assignment' | 'broker_single_district' | 'unknown'
  area_name: string | null
  season_year: number
  planting_date: string
  planted_area_rai: number | null
  cycle_days_p10: number | null
  cycle_days_p50: number | null
  cycle_days_p90: number | null
  cycle_source: 'empirical' | 'maturity_days' | 'default'
  arrival_date_p10: string | null
  arrival_date_p50: string | null
  arrival_date_p90: string | null
  projected_tons: number | null
}

export type SupplyChainFarmerPerformanceResponse = {
  id: string
  farmer_id: string
  agent_id: string | null
  agent_name: string | null
  broker_id: string | null
  broker_name: string | null
  province: string | null
  district: string | null
  district_id: string | null
  sowing_date: string | null
  est_harvest_date: string | null
  lf_est_harvest_date: string | null
  lf_maturity_days: number | null
  crop_type: string | null
  planted_area_rai: number | null
  estimated_yield_tons: number | null
  is_valid: boolean
  error_reason: string | null
}

export type SupplyChainClimateRisk =
  | 'heat'
  | 'cold'
  | 'flood'
  | 'drought'
  | 'pollination'
  | 'disease'
  | 'low_light'

export type SupplyChainClimateRiskResponse = {
  id: string
  district_id: string
  district_name: string | null
  district_en: string | null
  province_en: string | null
  week_start: string
} & Record<SupplyChainClimateRisk, number | null>

export type SupplyChainStatsInterval = 'day' | 'week' | 'month' | 'year'

export type SupplyChainPlantingDateField =
  | 'sowing_date'
  | 'est_harvest_date'
  | 'lf_est_harvest_date'

export type SupplyChainPlantingGroupBy =
  | 'none'
  | 'province'
  | 'district'
  | 'agent'
  | 'broker'

export type SupplyChainPlantingStatsRow = {
  period_start: string | null
  group_key: string | null
  group_label: string | null
  planted_area_rai: number | null
  estimated_yield_tons: number | null
  record_count: number
}

export type SupplyChainPlantingStatsResponse = {
  from: string
  to: string
  date_field: SupplyChainPlantingDateField
  interval: SupplyChainStatsInterval
  group_by: SupplyChainPlantingGroupBy
  totals: {
    planted_area_rai: number | null
    estimated_yield_tons: number | null
    record_count: number
    farmer_count: number
  }
  rows: SupplyChainPlantingStatsRow[]
}

export type SupplyChainHarvestGroupBy = 'none' | 'broker' | 'agronomist'

export type SupplyChainHarvestStatsRow = {
  period_start: string
  group_key: string | null
  group_label: string | null
  planned_tons: number | null
  actual_tons: number | null
  record_count: number
}

export type SupplyChainHarvestStatsResponse = {
  from: string
  to: string
  interval: SupplyChainStatsInterval
  group_by: SupplyChainHarvestGroupBy
  totals: {
    planned_tons: number | null
    actual_tons: number | null
    record_count: number
  }
  rows: SupplyChainHarvestStatsRow[]
}

export type SupplyChainEngineRunResponse = {
  id: string
  organization_id: string
  run_at: string
  as_of_date: string
  horizon_days: number
  reactive_enabled: boolean
  reactive_note: string | null
  preventive_note: string | null
  recommendation_count: number
}

export type SupplyChainEngineRecommendationResponse = {
  id: string
  organization_id: string
  engine_run_id: string
  run_at: string
  as_of_date: string
  lane: 'reactive' | 'preventive'
  action_type:
    | 'cross_broker_swap'
    | 'expedite'
    | 'shift_plant'
    | 'new_rai'
    | 'reduce_quota'
  target_date: string
  broker_id: string | null
  district_id: string | null
  sowing_date: string | null
  harvest_date: string | null
  rai: number | null
  expected_tons: number | null
  gap_resolved_tons: number | null
  confidence: number | null
  urgency: number | null
  rationale: string | null
  outcome_status: 'accepted' | 'rejected' | 'auto_expired' | null
  outcome_actual_tons: number | null
  outcome_evaluated_at: string | null
  outcome_user_id: string | null
}
